import { getSmartEdge } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/SmartEdge/getSmartEdge/getSmartEdge.ts';
import { EdgeLabel } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/SmartEdge/EdgeLabel/EdgeLabel.tsx';
import { BaseEdge, BezierEdge, Edge, EdgeProps, Node } from '@xyflow/react';
import { useMemo } from 'react';
import type { SmartEdgeOptions } from './SmartEdge.types';

interface Props<EdgeDataType extends Edge = Edge, NodeDataType extends Node = Node>
	extends EdgeProps<EdgeDataType> {
	nodes: Node<NodeDataType>[];
	options: SmartEdgeOptions;
}

export function SmartEdge<EdgeDataType extends Edge = Edge, NodeDataType extends Node = Node>({
	nodes,
	options,
	...edgeProps
}: Props<EdgeDataType, NodeDataType>) {
	const {
		sourceX,
		sourceY,
		sourcePosition,
		targetX,
		targetY,
		targetPosition,
		style,
		label,
		labelStyle,
		markerEnd,
		markerStart,
		interactionWidth,
		source,
		target,
	} = edgeProps;
	const FallbackEdge = options.fallback || BezierEdge;
	const isDragging = useMemo(
		() => !!nodes.find((n) => n.dragging && (n.id === source || n.id === target)),
		[nodes, source, target]
	);

	const smartResponse = useMemo(() => {
		return isDragging
			? null
			: getSmartEdge({
					sourcePosition,
					targetPosition,
					sourceX,
					sourceY,
					targetX,
					targetY,
					options: {
						...options,
						gridRatio: isDragging ? options.gridRatioDragging : options.gridRatio,
					},
					nodes,
				});
	}, [source, target, isDragging]);

	if (smartResponse === null) {
		return <FallbackEdge {...edgeProps} />;
	}

	const { edgeCenterX, edgeCenterY, svgPathString } = smartResponse;

	return (
		<>
			<BaseEdge
				path={svgPathString}
				style={style}
				markerStart={markerStart}
				markerEnd={markerEnd}
				interactionWidth={interactionWidth}
			/>
			{label && (
				<EdgeLabel
					edgeId={edgeProps.id}
					x={edgeCenterX}
					y={edgeCenterY}
					labelStyle={labelStyle}
				>
					{label}
				</EdgeLabel>
			)}
		</>
	);
}
